import React from 'react'

import { useDate } from '@hooks/dates'

import { Button, DateLabel, DatePickerContainer, SelectLabel } from './styles'

interface IProps {
  updatedDate: Date | undefined
  borderColor: string
  borderWidth: number
  onDateChange: (selectedDate: Date) => void
  mode: 'time' | 'date'
  minimumDate?: Date
  maximumDate?: Date
  isDisabled?: boolean
}

function toLocalDateString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const DatePicker = ({
  updatedDate,
  borderColor,
  borderWidth,
  onDateChange,
  isDisabled = false,
  mode,
  minimumDate,
  maximumDate,
}: IProps) => {
  const { formatDate } = useDate()

  const formattedDateOrTime =
    mode === 'date' && updatedDate
      ? formatDate(updatedDate).dateLong
      : updatedDate
        ? formatDate(updatedDate).time
        : undefined

  const dateLabel = mode === 'date' ? 'Select a date' : 'Select a time'

  const shouldShowDateOrLabel = formattedDateOrTime ?? dateLabel

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (!value) return

    if (mode === 'date') {
      const [year, month, day] = value.split('-').map(Number)
      onDateChange(new Date(year, month - 1, day))
    } else {
      const [hours, minutes] = value.split(':').map(Number)
      const date = updatedDate ? new Date(updatedDate) : new Date()
      date.setHours(hours, minutes)
      onDateChange(date)
    }
  }

  return (
    <DatePickerContainer
      $borderColor={borderColor}
      $borderWidth={borderWidth}
      $isDisabled={isDisabled}
    >
      <Button as="label" disabled={isDisabled}>
        {shouldShowDateOrLabel === dateLabel ? (
          <SelectLabel $isDisabled={isDisabled}>{dateLabel}</SelectLabel>
        ) : (
          <DateLabel $isDisabled={isDisabled}>{shouldShowDateOrLabel}</DateLabel>
        )}
        <input
          type={mode === 'date' ? 'date' : 'time'}
          disabled={isDisabled}
          onChange={handleChange}
          min={minimumDate ? (mode === 'date' ? toLocalDateString(minimumDate) : undefined) : undefined}
          max={maximumDate ? (mode === 'date' ? toLocalDateString(maximumDate) : undefined) : undefined}
          value={
            updatedDate
              ? mode === 'date'
                ? toLocalDateString(updatedDate)
                : `${String(updatedDate.getHours()).padStart(2, '0')}:${String(updatedDate.getMinutes()).padStart(2, '0')}`
              : ''
          }
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: 0,
            width: '100%',
            height: '100%',
            cursor: 'pointer',
          }}
        />
      </Button>
    </DatePickerContainer>
  )
}
